import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToggleSound } from "../Redux/actions/index";
import soundOneIcon from "../assets/soundOn.png";
import soundOffeIcon from "../assets/soundoff.png";
import { useNavigate } from "react-router-dom";
import { setQuestion, sutraMaster, sutraPoints } from "../Redux/actions/index";
import { auth } from "../firebase";
import { getSutraMasterArray } from "../components/dataUtils";

/**
 * Gamesteps component - Handles question progression, user progress saving, and navigation.
 * Props: Expects various step and question props from parent.
 */
export default function Gamesteps(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux selectors for audio toggle, sutra index, question number, and points
  const audiotoggle = useSelector((state) => state.soundToggle);
  const sutranumber = useSelector((state) => state.sutraMaster);
  const attemptedsutraquestion = useSelector((state) => state.questionNumber);
  const attemptsutraPoints = useSelector((state) => state.sutraPoints);

  // Local state for user info
  const [userName, setUserName] = useState("");
  const [authCode, setAuthCode] = useState("");

  // Memoize sutra master array for performance
  const sutramasterjsonfile = useMemo(() => getSutraMasterArray(), []);

  // Defensive checks for question validity and last question detection
  const isLastQuestion =
    props.nextcounter + 1 === props.QuestionsforSutra.length;
  const questionValid = !!props.QuestionsforSutra[props.nextcounter];

  /**
   * Handler to navigate to the next sutra in the sequence.
   * Increments sutra index, resets question, and navigates to new sutra route.
   * Alerts user if all sutras are completed.
   */
  const handleNextSutra = () => {
    const nextIndex = sutranumber + 1;
    dispatch(sutraMaster(nextIndex));
    dispatch(setQuestion(0));
    const [nextLevel, nextChapter, nextKey] =
      sutramasterjsonfile[nextIndex] || [];
    if (nextLevel && nextChapter && nextKey) {
      navigate(`/${nextLevel}/${nextChapter}/${nextKey}`);
    } else {
      alert("Congratulations! You have completed all sutras.");
    }
  };

  /**
   * Saves the user's current progress to Firebase.
   * Uses authenticated user's UID and ID token for secure, per-user storage.
   * Called on relevant progress changes.
   */
  const storegameDetails = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");
      const idToken = await user.getIdToken();

      // Parse current level/chapter/sutra from URL
      const urlParts = window.location.pathname
        .split("/")
        .filter((part) => part !== "");

      let attemptLevel = "level1";
      let attemptChapter = "chapter1";
      let attemptSutra = "1_4_3"; // Default if not found

      if (urlParts.length >= 3) {
        attemptLevel = urlParts[0];
        attemptChapter = urlParts[1];
        attemptSutra = urlParts[2];
      }

      const attemptData = {
        userid: user.uid,
        username: user.displayName || user.email || "Guest",
        attemptime: new Date().toTimeString().split(" ")[0],
        attemptdate: new Date().toISOString().split("T")[0],
        attemptlevel: attemptLevel,
        attemptchapter: attemptChapter,
        attemptsutra: attemptSutra,
        attemptquestion: props.nextcounter,
        sutrapoints: attemptsutraPoints,
      };

      // 1. Save current state (PUT)
      await fetch(
        `https://vedicmaths-9d086-default-rtdb.asia-southeast1.firebasedatabase.app/userProgress/${user.uid}.json?auth=${idToken}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(attemptData),
        }
      );

      // 2. Append to history (POST)
      await fetch(
        `https://vedicmaths-9d086-default-rtdb.asia-southeast1.firebaseio.com/sutrahistory/${user.uid}.json?auth=${idToken}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(attemptData),
        }
      );
    } catch (error) {
      console.error("Failed to save game details:", error);
    }
  };

  // On mount, load user info from localStorage (for display, not authentication)
  useEffect(() => {
    const authCode = window.localStorage.getItem("authCode");
    const userName = window.localStorage.getItem("name");
    setAuthCode(authCode);
    setUserName(userName);
  }, []);

  // Save progress to Firebase whenever the authenticated user or question changes
  useEffect(() => {
    if (auth.currentUser && props.nextcounter !== null) {
      storegameDetails();
    }
    // eslint-disable-next-line
  }, [auth.currentUser, props.nextcounter]);

  return (
    <section
      className="box-steps-input input-box-section col-12 col-sm-6"
      id="InputAnswerSection"
    >
      {/* If no valid question, show completion message */}
      {!questionValid ? (
        <div>All questions completed!</div>
      ) : isLastQuestion && props.currentStep > props.steps.length ? (
        // If last question and all steps done, show next sutra prompt
        <>
          <p>
            Congratulations, you have completed the sutra. Now you can move to
            the next sutra.
          </p>
          <button className="btn btn-outline-primary" onClick={handleNextSutra}>
            Next Sutra
          </button>
        </>
      ) : (
        <>
          <section>
            <div className="row">
              <div className="fw-bold mb-2 col-9">
                Solve the below using the sutra
              </div>
              <div className="col-3">
                {props.nextcounter + 1} / {props.QuestionsforSutra.length}
              </div>
              <div className="col-3"></div>
            </div>
          </section>
          {/* Display numbers for the question if provided */}
          {props.firstNumber ? (
            <p className="fw-bold text-black-50">
              First number: {props.firstNumber}
            </p>
          ) : null}
          {props.secondNumber ? (
            <p className="fw-bold text-black-50">
              Second number: {props.secondNumber}
            </p>
          ) : null}
          {props.thirdNumber ? (
            <p className="fw-bold text-black-50">
              Third number: {props.thirdNumber}
            </p>
          ) : null}
          {props.fourthNumber ? (
            <p className="fw-bold text-black-50">
              Fourth number: {props.fourthNumber}
            </p>
          ) : null}
          {/* Render step input boxes for the current step */}
          {props.currentStep < props.steps.length + 1 && (
            <>
              <span>{props.message}</span>
              {props.steps.map((stepcompletionMessage, index) => (
                <div className="row my-1" key={index} id="StepInputBox">
                  <div className="col-12 col-sm-6 my-2">
                    <p>{stepcompletionMessage}</p>
                  </div>
                  <div className="col-12 col-sm-6 my-1" id="InputBox">
                    <input
                      id={`inputstep${index}`}
                      ref={props.inputRef}
                      type="text"
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          props.handleStepInput(event.target.value, index);
                        }
                      }}
                      className="form-control width-input"
                      disabled={
                        index + 1 > props.currentStep ||
                        index + 1 < props.currentStep
                      }
                      autoFocus={props.currentStep === index + 1}
                    />
                  </div>
                </div>
              ))}
            </>
          )}
          {/* Show final answer and next button if all steps for the question are done */}
          {props.nextcounter + 1 !== props.QuestionsforSutra.length &&
            props.currentStep > props.steps.length && (
              <>
                <p className="fw-bold text-black-50">
                  Final answer: {props.finalNumber}
                </p>
                <p>{props.completionTextMessage}</p>
                <button
                  className="btn btn-outline-primary my-4"
                  onClick={props.HandleNextNumber}
                  id="BtnNext"
                >
                  Next
                </button>
              </>
            )}
        </>
      )}
    </section>
  );
}
